import { getScriptTurnText } from "@/types/ScriptTurn"

import type { EditableTestCase } from "../model"

interface TestCaseFilters {
  searchQuery: string
  typeFilters: string[]
}

// Known test case types get curated labels; unknown types fall back to a
// title-cased version of the raw value so new backend types still render.
const KNOWN_TYPE_LABELS: Record<string, string> = {
  SCENARIO: "Scenario",
  TRANSCRIPT: "Transcript",
  SCRIPT: "Script",
  WORKFLOW: "Workflow",
  IVR: "IVR",
}

export function formatTestCaseTypeLabel(type: string): string {
  const known = KNOWN_TYPE_LABELS[type]
  if (known) {
    return known
  }
  return type
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

/** Distinct `input_type` values present in the list, sorted for stable display. */
export function getAvailableTestCaseTypes(
  testCases: EditableTestCase[],
): string[] {
  const types = new Set<string>()
  for (const testCase of testCases) {
    if (testCase.input_type) {
      types.add(testCase.input_type)
    }
  }
  return Array.from(types).sort()
}

// Search scope mirrors FindReplace: simulation input, description, script
// turns, expected behaviors, and expected-output metadata keys/values.
function matchesSearch(
  testCase: EditableTestCase,
  normalizedQuery: string,
): boolean {
  if (!normalizedQuery) {
    return true
  }

  const haystacks: string[] = []
  if (testCase.input_str) {
    haystacks.push(testCase.input_str)
  }
  if (testCase.description) {
    haystacks.push(testCase.description)
  }
  for (const turn of testCase.script_turns ?? []) {
    const text = getScriptTurnText(turn)
    if (text) {
      haystacks.push(text)
    }
  }
  if (testCase.expected_behaviors?.length) {
    haystacks.push(testCase.expected_behaviors.join("\n"))
  }
  if (testCase.expected_output_json) {
    for (const [key, value] of Object.entries(testCase.expected_output_json)) {
      haystacks.push(key)
      if (typeof value === "string") {
        haystacks.push(value)
      } else if (value != null) {
        haystacks.push(JSON.stringify(value))
      }
    }
  }

  return haystacks.some((value) =>
    value.toLowerCase().includes(normalizedQuery),
  )
}

export function hasActiveTestCaseFilters({
  searchQuery,
  typeFilters,
}: TestCaseFilters): boolean {
  return searchQuery.trim().length > 0 || typeFilters.length > 0
}

/**
 * Returns the test cases that match the active filters. When no filters are
 * active the original array reference is returned unchanged so downstream
 * consumers can treat the result as a cheap view of the source list.
 */
export function filterTestCases(
  testCases: EditableTestCase[],
  { searchQuery, typeFilters }: TestCaseFilters,
): EditableTestCase[] {
  const normalizedQuery = searchQuery.trim().toLowerCase()
  const typeSet = typeFilters.length > 0 ? new Set(typeFilters) : null

  if (!normalizedQuery && !typeSet) {
    return testCases
  }

  return testCases.filter((testCase) => {
    if (typeSet && !typeSet.has(testCase.input_type)) {
      return false
    }
    return matchesSearch(testCase, normalizedQuery)
  })
}
